import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PageLayout from "@/components/PageLayout";
import SEO from "@/components/SEO";
import FAQSchema from "@/components/FAQSchema";
import ContactModal from "@/components/new-landing/ContactModal";
import { trackEvent } from "@/utils/analytics";

const NAVY = "#002178";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 },
};

const comparisonRows = [
  {
    label: "Core question",
    cpo: "What should we build and why?",
    cto: "How should we build it?",
  },
  {
    label: "Focus",
    cpo: "Product strategy, user outcomes, and business impact",
    cto: "Technical architecture, engineering processes, and infrastructure",
  },
  {
    label: "Deliverables",
    cpo: "Product roadmap, growth strategy, team alignment",
    cto: "System architecture, tech stack decisions, engineering hiring",
  },
  {
    label: "Reports to",
    cpo: "CEO or PE operating partner",
    cto: "CEO or CPO",
  },
  {
    label: "Best for",
    cpo: "Companies struggling with product direction, not technical execution",
    cto: "Companies that know what to build but need help building it well",
  },
];

const faqs = [
  {
    question: "What is the difference between a CPO and a CTO?",
    answer:
      "The CPO decides what to build and why, based on user needs, market dynamics, and business goals. The CTO decides how to build it, owning architecture, engineering processes, and technical infrastructure. The CPO is accountable for product outcomes; the CTO is accountable for technical execution.",
  },
  {
    question: "Can a CTO handle product strategy?",
    answer:
      "Some CTOs have strong product instincts, but product strategy is a distinct discipline that requires deep expertise in user research, market positioning, and business model design. When a CTO owns product strategy, technical feasibility often drives decisions instead of user and business value. The result is a product that's well-built but doesn't grow.",
  },
  {
    question:
      "Do PE-backed companies need both FractionalCPO and a fractional CTO?",
    answer:
      "It depends on where the gap is. If the technology works but the product isn't driving growth, you need FractionalCPO. If the product direction is clear but engineering can't deliver, you need a CTO. PE-backed companies going through transformation often benefit from both, with FractionalCPO setting direction and the CTO ensuring the team can execute on it.",
  },
];

const FractionalCPOvsCTO = () => {
  const [isContactOpen, setIsContactOpen] = useState(false);

  return (
    <PageLayout>
      <SEO
        title="Fractional CPO vs Fractional CTO: Strategy vs Architecture"
        description="Understand the difference between a Fractional CPO and Fractional CTO. Learn when your technology company needs product strategy vs technical leadership."
        path="/fractional-cpo-vs-cto"
        schema={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Fractional CPO vs Fractional CTO: Strategy vs Architecture",
          "url": "https://fractionalcpo.com/fractional-cpo-vs-cto",
          "dateModified": "2026-06-17",
          "description": "Understand the difference between a Fractional CPO and Fractional CTO. Learn when your technology company needs product strategy vs technical leadership.",
          "isPartOf": { "@type": "WebSite", "url": "https://fractionalcpo.com" }
        }}
      />

      {/* Hero */}
      <section className="py-24 md:py-40 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1400px] mx-auto">
          <motion.p
            {...fadeUp}
            className="text-sm uppercase tracking-[0.3em] text-[#002178] mb-8 font-medium"
          >
            Comparison
          </motion.p>

          <motion.h1
            {...fadeUp}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-[clamp(2.5rem,8vw,6rem)] font-light leading-[0.95] tracking-[-0.03em] mb-12"
          >
            Fractional CPO vs{" "}
            <span className="text-[#002178] font-medium">Fractional CTO</span>
          </motion.h1>

          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="max-w-3xl"
          >
            <p className="text-xl md:text-2xl leading-relaxed font-medium text-[#1a1a1a] mb-6">
              Strategy vs. architecture. What to build vs. how to build it.
            </p>
            <p className="text-lg text-black/60 leading-relaxed mb-4">
              The CPO owns the "what" and the "why." What problems are we
              solving? What should we build next? Why does this matter to the
              business?
            </p>
            <p className="text-lg text-black/60 leading-relaxed">
              The CTO owns the "how." How do we architect the system? How do
              we scale the engineering team? How do we maintain velocity without
              sacrificing quality?
            </p>
          </motion.div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#F5F5F0]">
        <div className="max-w-[1400px] mx-auto">
          <motion.h2
            {...fadeUp}
            className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] mb-16"
          >
            How they{" "}
            <span className="text-[#002178] font-medium">compare</span>
          </motion.h2>

          {/* Desktop Table */}
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="hidden md:block overflow-x-auto"
          >
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b-2 border-black/10">
                  <th className="text-left py-4 pr-6 text-sm uppercase tracking-[0.2em] text-black/40 font-medium w-[20%]" />
                  <th className="text-left py-4 px-4 text-sm uppercase tracking-[0.2em] text-[#002178] font-medium w-[40%]">
                    Fractional CPO
                  </th>
                  <th className="text-left py-4 px-4 text-sm uppercase tracking-[0.2em] text-black/40 font-medium w-[40%]">
                    Fractional CTO
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row, i) => (
                  <tr
                    key={i}
                    className="border-b border-black/5 hover:bg-black/[0.02] transition-colors"
                  >
                    <td className="py-5 pr-6 text-sm font-medium text-black/60">
                      {row.label}
                    </td>
                    <td className="py-5 px-4 text-sm font-medium text-[#1a1a1a]">
                      {row.cpo}
                    </td>
                    <td className="py-5 px-4 text-sm text-black/50">
                      {row.cto}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-6">
            {comparisonRows.map((row, i) => (
              <motion.div
                key={i}
                {...fadeUp}
                transition={{ delay: i * 0.08 }}
                className="border-b border-black/10 pb-6"
              >
                <p className="text-sm font-medium text-black/40 uppercase tracking-[0.15em] mb-3">
                  {row.label}
                </p>
                <div className="space-y-3">
                  <div className="bg-[#002178]/[0.04] p-3 rounded">
                    <p className="text-xs text-[#002178] font-medium mb-1">
                      Fractional CPO
                    </p>
                    <p className="text-sm font-medium">{row.cpo}</p>
                  </div>
                  <div className="p-3">
                    <p className="text-xs text-black/40 font-medium mb-1">
                      Fractional CTO
                    </p>
                    <p className="text-sm text-black/60">{row.cto}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* When You Need a CPO, Not a CTO */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid md:grid-cols-2 gap-16 md:gap-24 items-start">
            <div>
              <motion.h2
                {...fadeUp}
                className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] mb-8"
              >
                When you need a CPO,{" "}
                <span className="text-[#002178] font-medium">not a CTO</span>
              </motion.h2>
              <motion.div
                {...fadeUp}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="space-y-4"
              >
                <p className="text-lg text-black/60 leading-relaxed">
                  The technology works fine. The engineering team ships on time.
                  But the product still isn't driving growth.
                </p>
                <p className="text-lg text-black/60 leading-relaxed">
                  When the problem is product direction, not technical
                  execution, you need someone who can connect user needs to
                  business outcomes. Someone who knows what to build next and
                  why it matters.
                </p>
                <p className="text-lg text-black/60 leading-relaxed">
                  A CTO can build anything. A CPO ensures you're building the
                  right thing.
                </p>
              </motion.div>
            </div>

            <div>
              <motion.h2
                {...fadeUp}
                className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] mb-8"
              >
                When you need{" "}
                <span className="text-[#002178] font-medium">both</span>
              </motion.h2>
              <motion.div
                {...fadeUp}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="space-y-4"
              >
                <p className="text-lg text-black/60 leading-relaxed">
                  PE-backed technology companies going through transformation
                  often need both roles. The CPO sets direction; the CTO
                  ensures the team can execute on it.
                </p>
                <p className="text-lg text-black/60 leading-relaxed">
                  This is especially common post-acquisition, when a company
                  needs to modernize its product while simultaneously
                  rethinking its product strategy.
                </p>
                <p className="text-lg text-black/60 leading-relaxed">
                  The CPO and CTO partnership works best when the CPO defines
                  the outcomes and the CTO defines the path to get there. Clear
                  accountability, shared context, different expertise.
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        className="py-24 md:py-32 px-6 md:px-12 lg:px-24"
        style={{ backgroundColor: NAVY }}
      >
        <div className="max-w-[1400px] mx-auto">
          <div className="max-w-3xl">
            <motion.h2
              {...fadeUp}
              className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] text-white mb-8"
            >
              Not sure which role{" "}
              <span className="font-medium">you need?</span>
            </motion.h2>

            <motion.p
              {...fadeUp}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-lg text-white/70 leading-relaxed mb-8"
            >
              We'll help you figure it out. Our{" "}
              <Link
                to="/assessment"
                className="text-white underline underline-offset-4 hover:text-white/90"
              >
                product assessment
              </Link>{" "}
              identifies whether your growth gap is a product problem, a
              technical problem, or both.
            </motion.p>

            <motion.div {...fadeUp} transition={{ duration: 0.6, delay: 0.2 }}>
              <button
                onClick={() => { trackEvent('contact_click', { source: 'cta_block' }); setIsContactOpen(true); }}
                className="inline-block px-8 py-4 bg-white text-[#002178] font-medium hover:scale-105 transition-transform"
              >
                Let's chat
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <div className="bg-[#F5F5F0]">
        <FAQSchema faqs={faqs} heading="Frequently Asked Questions" />
      </div>

      <ContactModal open={isContactOpen} onOpenChange={setIsContactOpen} />
    </PageLayout>
  );
};

export default FractionalCPOvsCTO;
